/* ============
 * Bootstrap File
 * ============
 *
 * Will configure and bootstrap the application
 */

/* ============
 * Vue
 * ============
 *
 * Vue.js is a library for building interactive web interfaces.
 * It provides data-reactive components with a simple and flexible API.
 *
 * http://rc.vuejs.org/guide/
 */
import Vue from 'vue';
import config from 'services/apiConfigs';
import { environment } from 'services/apiConfigs';
import { EventBus } from './app/utils/event-bus';

// Global directive: autofocusses element when it comes in
Vue.directive('focus', {
  // When the bound element is inserted into the DOM...
  inserted(el) {
    // Focus the element
    el.focus();
  },
});

import { ToggleButton } from 'vue-js-toggle-button';
Vue.component('ToggleButton', ToggleButton);

/* ============
 * Vuex Router Sync
 * ============
 *
 * Effortlessly keep vue-Router and vuex store in sync.
 *
 * https://github.com/vuejs/vuex-router-sync/blob/master/README.md
 */
import VuexRouterSync from 'vuex-router-sync';
import store from './app/store';

const invite_code_key = 'invite_code';

// This function checks to see if the user is attemping to access a roundtable
// ot a pre-defined location in the app through a magic link, or from an email
// redirect. If either of those things are true, we atore the location of the destination
// in local storage to activate once the user has logged in.
// This will be performed inthe index.side-effects.js file in a function called checkMagicLink
const checkRedirects = (to) => {
  if (to.query.invite_code) {
    store
      .dispatch('validateCode', to.query.invite_code)
      .then(() => {
        localStorage.setItem(invite_code_key, to.query.invite_code);

        setTimeout(() => {
          EventBus.$emit(
            'toast-msg',
            'Sign in or register to join this RoundTable.'
          );
        }, 400);
      })
      .catch((error) => {
        try {
          localStorage.removeItem(invite_code_key);

          console.log(error);

          // TODO: Revert to this message once we figure out what is causing the invite
          // errors.
          // let error_message = 'An error has occurred.';

          let error_message = error;

          if (error && error.request && error.request.status) {
            const error_status_code = error.request.status;

            switch (error_status_code) {
              case 400:
                error_message = 'No invite code was sent.';
                break;
              case 401:
                error_message = 'An authentication error has occurred.';
                break;
              case 403:
                error_message = 'Invalid code. Please use a valid invite code.';
                break;
              case 408:
                error_message = 'The request has timed out. Please try again.';
                break;
              default:
                error_message = error + ' ' + error.request.response;
                break;
            }
          }

          EventBus.$emit('toast-msg', error_message);
        } catch (error) {
          EventBus.$emit('toast-msg', error);
        }
      });
  }

  if (to.query.from_email) {
    localStorage.setItem('emailRedirect', to.fullPath);
  }
};

// Do not show devtools on prod
if (environment === 'production') {
  Vue.config.devtools = false;
  Vue.config.debug = false;
  Vue.config.silent = true;
}

Vue.filter('truncate', (value, maxChar) => {
  if (!value) {
    return '';
  } else if (value.length > maxChar) {
    return `${value.slice(0, maxChar)}...`;
  }
  return value;
});

/* ============
 * v-tooltips
 * ============
 */
import { VTooltip } from 'v-tooltip';

Vue.directive('my-tooltip', VTooltip);

/* ============
 * Axios
 * ============
 *
 * Promise based HTTP client for the browser and node.js.
 * Because Vue Resource has been retired, Axios will now been used
 * to perform AJAX-requests.
 *
 * https://github.com/mzabriskie/axios
 */
import Axios from 'axios';

Axios.defaults.baseURL = config.apiURL;
Axios.defaults.headers.common.Accept = 'application/json';
// TODO: if the requests are missing headers
// Axios.interceptors.request.use();
Vue.$http = Axios;

store.dispatch('checkAuthentication');

/* ============
 * Vue Router
 * ============
 *
 * The official Router for Vue.js. It deeply integrates with Vue.js core
 * to make building Single Page Applications with Vue.js a breeze.
 *
 * http://router.vuejs.org/en/index.html
 */
import Cookie from 'js-cookie';
import VueRouter from 'vue-router';
import loadUserInfo from 'app/services/account/completeInfo';
import trackActiveTime from 'app/services/account/trackActiveTime';
import routes from './app/routes';

const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

let trackingTime = false;

// We send an API request to track the user's activity every 15 minutes (900000ms).
// This function works recursively.
const trackUserTime = () => {
  sleep(900000).then(() => {
    console.log('15 minutes have passed. Tracking time.');

    // We need the token to make sure the user has not logged out in the meantime.
    const token = localStorage.getItem('_prly_tk');

    if (!token) {
      trackingTime = false;
    }

    if (trackingTime && token) {
      trackActiveTime().then(() => {
        return trackUserTime();
      });
    }
  });
};

Vue.use(VueRouter);

export const router = new VueRouter({
  routes,
  mode: 'history',
  linkActiveClass: 'active'
});

router.beforeEach(async (to, from, next) => {


  // go to the top of the page
  window.scrollTo(0, 0);

  if (to.query && to.query._prly_tk) {
    // Cookie.set('_prly_tk', to.query._prly_tk);
    localStorage.setItem('_prly_tk', to.query._prly_tk);
    store.dispatch('checkAuthentication');
  }

  // auth variables
  const cookie = Cookie.get('_prly_tk');
  const token = localStorage.getItem('_prly_tk') || cookie;
  const authRequired = to.matched.some((m) => m.meta.auth);
  const { checked, authenticated } = store.state.auth;

  // if the token exists but the state says the user isn't authenticated
  // reload the user's info
  if (token && !authenticated) {
    trackingTime = false;

    await loadUserInfo().then(async(res) => {
      const account = res.data;
      const teacherOnlyRoutes = ['roundtable.edit', 'live.edit'];
      store.dispatch('setAccount', account);
      if (account.role === 'student' && teacherOnlyRoutes.includes(to.name)) {
        this.$router.push({ name: 'dashboard' });
      }
      return null;
    })
      .catch((err) => {
        console.error(err);
        store.dispatch('logout');
        return Vue.router.go('login');
      });
    return next();
  }
  if (authRequired && !authenticated && checked && !token) {
    trackingTime = false;

    /*
     * If a user tries to use a magic link to access an activity, but they are not
     * authenticated, we will store that in local data so we can redirect post login/register
     */
    checkRedirects(to);
    // Store the premade id in local storage and alert user if they are not logged in
    if (to.query.premade_id) {
      localStorage.setItem('premade_id', to.query.premade_id);
      setTimeout(() => {
        EventBus.$emit(
          'toast-msg',
          'Sign in or register to create this RoundTable'
        );
      }, 400);
    }

    if (to.query.open_ai) {
      localStorage.setItem('open_ai', to.query.open_ai);
      localStorage.setItem('open_ai', to.query.type);
    }

    /*
     * If the user is not authenticated and visits
     * a page that requires authentication, redirect to the login page
     */
    return next({
      name: 'login',
    });
  }

  // If user is logged in and tracking time has not yet been enabled.
  if (token && authenticated && !trackingTime) {
    trackingTime = true;

    console.log('Track time job registered successfully.');

    // const profile_data = await loadUserInfo();
    // const account = profile_data.data;
    // await store.dispatch('setAccount', account);
    // await store.dispatch('setAccountLoading', false);
    // const role = account.role;
    // if (role === 'teacher') {
    //   const updated_sujects_grades = account.updated_subjects_grades;
    //   const grades = account.grade_levels.length;
    //   const subjects = account.subjects.length;
    //   if (!grades || !subjects || !updated_sujects_grades) {
    //     return next({
    //       name: 'dashboard',
    //     });
    //   }
    // }

    trackUserTime();
  }

  return next();
});
VuexRouterSync.sync(store, router);

Vue.router = router;

/* ============
 * Vue i18n
 * ============
 *
 * Internationalization plugin of Vue.js
 *
 * https://kazupon.github.io/vue-i18n/
 */
// NOTE: Removed because it messes up VUE
// import VueI18n from 'vue-i18n';
// import locale from './app/locale';
//
// Vue.use(VueI18n);
//
// Vue.config.lang = 'en';
//
// Object.keys(locale).forEach((lang) => {
//   Vue.locale(lang, locale[lang]);
// });

require('flexboxgrid');

/**
  TinyMCE Module
 */
//
// import 'tinymce/tinymce';
// import 'tinymce/themes/modern';
// import 'tinymce/skins/lightgray/skin.min.css';
// // plugins
// import 'tinymce/plugins/link';
// import 'tinymce/plugins/image';
// import 'tinymce/plugins/media';
// import 'tinymce/plugins/paste';
// import 'tinymce/plugins/autolink';
// import 'tinymce/plugins/lists';
// import 'tinymce/plugins/advlist';
// import 'tinymce/plugins/autoresize';

/**
 * VUE Socket IO
 */

// NOTE: socketio commented out for now
// import VueSocketio from 'vue-socket.io';
//
// // We import the official client so we can bind to the vue-socket-io wrapper
// // We do this because the wrapper is limited in configuring the options for the client
// import io from 'socket.io-client';
//
// const socket = io(`${config.apiURL}`, {
//   // transports: ['websocket'],
//   transports: ['polling', 'websocket'],
//   secure: true,
// });
// Vue.use(VueSocketio, socket);

// Parlay Material
import ParlayMaterial from 'components/material/index';

Vue.use(ParlayMaterial);

window.renderMath = () => {
  setTimeout(() => {
    window.MathJax.typeset();
  }, 1000);
};

/* import VueVirtualScroller from 'vue-virtual-scroller';

Vue.use(VueVirtualScroller); */

window.helpcrunchSettings = {
  organization: 'parlay',
  appId: 'cdce9166-6cde-4e65-a478-535f99424b42',
};
(function (w, d) {
  var hS = w.helpcrunchSettings;
  if (!hS || !hS.organization) {
    return;
  }
  var widgetSrc =
    'https://' + hS.organization + '.widget.helpcrunch.com/';
  w.HelpCrunch = function () {
    w.HelpCrunch.q.push(arguments);
  };
  w.HelpCrunch.q = [];
  function r() {
    if (d.querySelector('script[src="' + widgetSrc + '"')) {
      return;
    }
    var s = d.createElement('script');
    s.async = 1;
    s.type = 'text/javascript';
    s.src = widgetSrc;
    (d.body || d.head).appendChild(s);
  }
  if (d.readyState === 'complete' || hS.loadImmediately) {
    r();
  } else if (w.attachEvent) {
    w.attachEvent('onload', r);
  } else {
    w.addEventListener('load', r, false);
  }
})(window, document);

export default {
  router,
};



// WEBPACK FOOTER //
// ./src/bootstrap.js